import React, { useState, useEffect } from 'react'
import {
  Settings,
  Shield,
  Bot,
  Database,
  Bell,
  Users,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react'
import { settingsService, SystemSettings, AISettings, DJENSettings, RolePermissions, SecuritySettings, NotificationSettings } from '../services/settingsService'
import { djenSyncService } from '../services/djen-sync.service'

import { getErrorMessage } from '../utils/error.utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { FormField, FormSection } from '@/components/common/FormSection'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

interface ToggleRowProps {
  id: string
  label: string
  description: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}

/** Linha de configuração liga/desliga (checkbox com aparência de interruptor). */
function ToggleRow({ id, label, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <label className="relative inline-flex shrink-0 cursor-pointer items-center">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
          disabled={disabled}
        />
        <span
          aria-hidden="true"
          className="h-6 w-11 rounded-full bg-input transition-colors after:absolute after:left-0.5 after:top-0.5 after:size-5 after:rounded-full after:bg-background after:shadow-sm after:transition-transform peer-checked:bg-primary peer-checked:after:translate-x-5 peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
        />
      </label>
    </div>
  )
}

interface NumberFieldProps {
  id: string
  label: string
  hint?: string
  min: number
  max: number
  value: number
  disabled: boolean
  onChange: (value: number) => void
}

function NumberField({ id, label, hint, min, max, value, disabled, onChange }: NumberFieldProps) {
  return (
    <FormField label={label} htmlFor={id} hint={hint}>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-32"
        disabled={disabled}
      />
    </FormField>
  )
}

export const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const data = await settingsService.getSettings()
      setSettings(data)
    } catch (error) {
      console.error('Error loading settings:', error)
      setError('Erro ao carregar configurações')
    } finally {
      setLoading(false)
    }
  }

  const showMessage = (message: string, isError = false) => {
    if (isError) {
      setError(message)
      setSuccess(null)
    } else {
      setSuccess(message)
      setError(null)
    }
    setTimeout(() => {
      setError(null)
      setSuccess(null)
    }, 3000)
  }

  const updateAISettings = async (newSettings: Partial<AISettings>) => {
    if (!settings) return

    try {
      setSaving(true)
      const updatedAI = { ...settings.ai, ...newSettings }
      await settingsService.updateAISettings(updatedAI)
      setSettings({ ...settings, ai: updatedAI })
      showMessage('Configurações de IA atualizadas com sucesso')
    } catch (error) {
      console.error('Error updating AI settings:', error)
      showMessage('Erro ao atualizar configurações de IA', true)
    } finally {
      setSaving(false)
    }
  }

  const updateDJENSettings = async (newSettings: Partial<DJENSettings>) => {
    if (!settings) return

    try {
      setSaving(true)
      const updatedDJEN = { ...settings.djen, ...newSettings }
      await settingsService.updateDJENSettings(updatedDJEN)
      setSettings({ ...settings, djen: updatedDJEN })
      showMessage('Configurações do DJEN atualizadas com sucesso')
    } catch (error) {
      console.error('Error updating DJEN settings:', error)
      showMessage('Erro ao atualizar configurações do DJEN', true)
    } finally {
      setSaving(false)
    }
  }

  const handleManualSync = async () => {
    try {
      setSyncing(true)
      setSyncResult(null)
      setError(null)
      setSuccess(null)

      const result = await djenSyncService.triggerManualSync()

      if (result.success) {
        setSyncResult(`Sincronização concluída com sucesso! ${result.message}`)
        showMessage('Sincronização manual executada com sucesso')
      } else {
        setSyncResult(`Falha na sincronização: ${result.message}`)
        showMessage('Falha na sincronização manual', true)
      }
    } catch (error: unknown) {
      console.error('Error during manual sync:', error)
      setSyncResult(`Erro na sincronização: ${getErrorMessage(error)}`)
      showMessage('Erro ao executar sincronização manual', true)
    } finally {
      setSyncing(false)
    }
  }

  const updatePermissions = async (newPermissions: RolePermissions) => {
    if (!settings) return

    try {
      setSaving(true)
      await settingsService.updatePermissions(newPermissions)
      setSettings({ ...settings, permissions: newPermissions })
      showMessage('Permissões atualizadas com sucesso')
    } catch (error) {
      console.error('Error updating permissions:', error)
      showMessage('Erro ao atualizar permissões', true)
    } finally {
      setSaving(false)
    }
  }

  const updateSecuritySettings = async (newSettings: Partial<SecuritySettings>) => {
    if (!settings) return

    try {
      setSaving(true)
      const updatedSecurity: SecuritySettings = { ...settings.security, ...newSettings }
      await settingsService.updateSecuritySettings(updatedSecurity)
      setSettings({ ...settings, security: updatedSecurity })
      showMessage('Configurações de segurança atualizadas com sucesso')
    } catch (error) {
      console.error('Error updating security settings:', error)
      showMessage('Erro ao atualizar configurações de segurança', true)
    } finally {
      setSaving(false)
    }
  }

  const updateNotificationSettings = async (newSettings: Partial<NotificationSettings>) => {
    if (!settings) return

    try {
      setSaving(true)
      const updatedNotifications: NotificationSettings = { ...settings.notifications, ...newSettings }
      await settingsService.updateNotificationSettings(updatedNotifications)
      setSettings({ ...settings, notifications: updatedNotifications })
      showMessage('Configurações de notificações atualizadas com sucesso')
    } catch (error) {
      console.error('Error updating notification settings:', error)
      showMessage('Erro ao atualizar configurações de notificações', true)
    } finally {
      setSaving(false)
    }
  }

  const handlePermissionChange = (role: string, permission: string, value: boolean) => {
    if (!settings) return
    if (saving) return

    const newPermissions: RolePermissions = {
      ...settings.permissions,
      [role]: {
        ...settings.permissions[role],
        [permission]: value
      }
    }
    updatePermissions(newPermissions)
  }

  const roleLabels = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Administrador',
    LAWYER: 'Advogado',
    ASSISTANT: 'Assistente',
    CLIENT: 'Cliente',
    USER: 'Usuário'
  }

  const permissionLabels = {
    canViewPublications: 'Ver Publicações',
    canEditPublications: 'Editar Publicações',
    canDeletePublications: 'Excluir Publicações',
    canViewUsers: 'Ver Usuários',
    canEditUsers: 'Editar Usuários',
    canDeleteUsers: 'Excluir Usuários',
    canViewClients: 'Ver Clientes',
    canEditClients: 'Editar Clientes',
    canDeleteClients: 'Excluir Clientes',
    canViewCases: 'Ver Casos',
    canEditCases: 'Editar Casos',
    canDeleteCases: 'Excluir Casos',
    canViewFinance: 'Ver Financeiro',
    canEditFinance: 'Editar Financeiro',
    canDeleteFinance: 'Excluir Financeiro',
    canViewReports: 'Ver Relatórios',
    canViewSettings: 'Ver Configurações',
    canEditSettings: 'Editar Configurações'
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando configurações...</span>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10" />
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center" role="alert">
            <AlertTriangle className="size-10 text-destructive" aria-hidden="true" />
            <p className="text-sm font-medium">Erro ao carregar configurações do sistema</p>
            <Button variant="outline" className="mt-2" onClick={loadSettings}>Tentar novamente</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Settings className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Configurações do Sistema</h1>
          </div>
          <p className="text-sm text-muted-foreground">Configure as funcionalidades e permissões do LawPG</p>
        </div>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <AlertTriangle className="size-4 shrink-0 text-destructive" aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words text-destructive">{error}</span>
          <Button variant="ghost" size="icon-sm" aria-label="Fechar mensagem" onClick={() => setError(null)}>
            <X className="size-4" />
          </Button>
        </div>
      )}

      {success && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <CheckCircle className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words">{success}</span>
          <Button variant="ghost" size="icon-sm" aria-label="Fechar mensagem" onClick={() => setSuccess(null)}>
            <X className="size-4" />
          </Button>
        </div>
      )}

      <Tabs defaultValue="ai" className="min-w-0 gap-4">
        <div className="min-w-0 overflow-x-auto border-b border-border">
          <TabsList variant="line" className="h-auto min-w-max justify-start">
            <TabsTrigger value="ai" className="gap-2 py-2"><Bot className="size-4" /> Inteligência Artificial</TabsTrigger>
            <TabsTrigger value="djen" className="gap-2 py-2"><Database className="size-4" /> DJEN</TabsTrigger>
            <TabsTrigger value="permissions" className="gap-2 py-2"><Users className="size-4" /> Permissões</TabsTrigger>
            <TabsTrigger value="security" className="gap-2 py-2"><Shield className="size-4" /> Segurança</TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 py-2"><Bell className="size-4" /> Notificações</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="ai">
          <FormSection title="Configurações de IA" icon={Bot}>
            <ToggleRow
              id="settings-ai-auto-analysis"
              label="Análise Automática de Publicações"
              description="Permitir que a IA analise automaticamente as publicações do DJEN"
              checked={settings?.ai?.enableAutoAnalysis || false}
              onChange={(checked) => updateAISettings({ enableAutoAnalysis: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-ai-smart-filtering"
              label="Filtro Inteligente"
              description="Usar IA para filtrar publicações relevantes"
              checked={settings?.ai?.enableSmartFiltering || false}
              onChange={(checked) => updateAISettings({ enableSmartFiltering: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-ai-auto-classification"
              label="Classificação Automática"
              description="Classificar automaticamente o tipo de publicação"
              checked={settings?.ai?.autoClassification || false}
              onChange={(checked) => updateAISettings({ autoClassification: checked })}
              disabled={saving}
            />
            <FormField
              label={`Limite de Confiança (${Math.round((settings?.ai?.confidenceThreshold || 0.8) * 100)}%)`}
              htmlFor="settings-ai-confidence"
              hint="Define a confiança mínima necessária para a IA processar automaticamente"
            >
              <input
                id="settings-ai-confidence"
                type="range"
                min="0.5"
                max="0.95"
                step="0.05"
                value={settings?.ai?.confidenceThreshold || 0.8}
                onChange={(e) => updateAISettings({ confidenceThreshold: parseFloat(e.target.value) })}
                className="w-full cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                disabled={saving}
              />
            </FormField>
          </FormSection>
        </TabsContent>

        <TabsContent value="djen">
          <FormSection title="Configurações do DJEN" icon={Database}>
            <ToggleRow
              id="settings-djen-auto-sync"
              label="Sincronização Automática"
              description="Sincronizar automaticamente com a API do PJE"
              checked={settings?.djen?.enableAutoSync || false}
              onChange={(checked) => updateDJENSettings({ enableAutoSync: checked })}
              disabled={saving}
            />
            <NumberField
              id="settings-djen-sync-interval"
              label="Intervalo de Sincronização (minutos)"
              hint="Frequência de verificação de novas publicações (5-1440 minutos)"
              min={5}
              max={1440}
              value={settings?.djen?.syncInterval || 240}
              onChange={(value) => updateDJENSettings({ syncInterval: value })}
              disabled={saving}
            />
            <NumberField
              id="settings-djen-retry"
              label="Tentativas de Retry"
              hint="Número máximo de tentativas em caso de falha"
              min={1}
              max={10}
              value={settings?.djen?.maxRetryAttempts || 3}
              onChange={(value) => updateDJENSettings({ maxRetryAttempts: value })}
              disabled={saving}
            />
            <NumberField
              id="settings-djen-timeout"
              label="Timeout (segundos)"
              hint="Tempo limite para requisições à API do PJE"
              min={10}
              max={300}
              value={settings?.djen?.timeoutSeconds || 30}
              onChange={(value) => updateDJENSettings({ timeoutSeconds: value })}
              disabled={saving}
            />

            <div className="flex flex-col gap-3 border-t border-border pt-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-sm font-medium">Sincronização Manual</h3>
                  <p className="text-sm text-muted-foreground">
                    Execute uma sincronização imediata de todas as publicações
                  </p>
                </div>
                <Button onClick={handleManualSync} disabled={syncing || saving} className="self-start sm:self-auto">
                  <RefreshCw className={syncing ? 'size-4 animate-spin' : 'size-4'} />
                  {syncing ? 'Sincronizando...' : 'Sincronizar Agora'}
                </Button>
              </div>

              {syncResult && (
                <div className="rounded-lg border border-border bg-muted p-3">
                  <p className="break-words text-sm">{syncResult}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{new Date().toLocaleString('pt-BR')}</p>
                </div>
              )}
            </div>
          </FormSection>
        </TabsContent>

        <TabsContent value="permissions">
          <FormSection title="Matriz de Permissões por Perfil" icon={Users}>
            <div className="min-w-0 overflow-x-auto">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Permissão</TableHead>
                    {Object.keys(roleLabels).map(role => (
                      <TableHead key={role} className="text-center">
                        {roleLabels[role as keyof typeof roleLabels]}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.keys(permissionLabels).map(permission => {
                    const permissionLabel = permissionLabels[permission as keyof typeof permissionLabels]
                    return (
                      <TableRow key={permission}>
                        <TableCell className="font-medium">{permissionLabel}</TableCell>
                        {Object.keys(roleLabels).map(role => (
                          <TableCell key={`${role}-${permission}`} className="text-center">
                            <input
                              type="checkbox"
                              aria-label={`${permissionLabel} — ${roleLabels[role as keyof typeof roleLabels]}`}
                              checked={(settings?.permissions?.[role] as Record<string, boolean> | undefined)?.[permission] || false}
                              onChange={(e) => handlePermissionChange(role, permission, e.target.checked)}
                              className="size-4 cursor-pointer rounded-sm border-input accent-primary disabled:cursor-not-allowed"
                              disabled={saving}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </FormSection>
        </TabsContent>

        <TabsContent value="security">
          <FormSection title="Configurações de Segurança" icon={Shield}>
            <NumberField
              id="settings-security-password-length"
              label="Tamanho Mínimo da Senha"
              min={6}
              max={20}
              value={settings?.security?.passwordMinLength || 8}
              onChange={(value) => updateSecuritySettings({ passwordMinLength: value })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-security-special-chars"
              label="Exigir Caracteres Especiais"
              description="Passwords devem conter símbolos especiais"
              checked={settings?.security?.passwordRequireSpecialChars || false}
              onChange={(checked) => updateSecuritySettings({ passwordRequireSpecialChars: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-security-numbers"
              label="Exigir Números"
              description="Passwords devem conter números"
              checked={settings?.security?.passwordRequireNumbers || false}
              onChange={(checked) => updateSecuritySettings({ passwordRequireNumbers: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-security-uppercase"
              label="Exigir Letras Maiúsculas"
              description="Passwords devem conter letras maiúsculas"
              checked={settings?.security?.passwordRequireUppercase || false}
              onChange={(checked) => updateSecuritySettings({ passwordRequireUppercase: checked })}
              disabled={saving}
            />
            <NumberField
              id="settings-security-session-timeout"
              label="Timeout da Sessão (minutos)"
              min={15}
              max={480}
              value={settings?.security?.sessionTimeoutMinutes || 120}
              onChange={(value) => updateSecuritySettings({ sessionTimeoutMinutes: value })}
              disabled={saving}
            />
            <NumberField
              id="settings-security-max-attempts"
              label="Máximo de Tentativas de Login"
              min={3}
              max={10}
              value={settings?.security?.maxLoginAttempts || 5}
              onChange={(value) => updateSecuritySettings({ maxLoginAttempts: value })}
              disabled={saving}
            />
            <NumberField
              id="settings-security-lockout"
              label="Duração do Bloqueio (minutos)"
              min={5}
              max={60}
              value={settings?.security?.lockoutDurationMinutes || 15}
              onChange={(value) => updateSecuritySettings({ lockoutDurationMinutes: value })}
              disabled={saving}
            />
          </FormSection>
        </TabsContent>

        <TabsContent value="notifications">
          <FormSection title="Configurações de Notificações" icon={Bell}>
            <ToggleRow
              id="settings-notifications-email"
              label="Notificações por Email"
              description="Enviar notificações por email para eventos importantes"
              checked={settings?.notifications?.emailNotifications || false}
              onChange={(checked) => updateNotificationSettings({ emailNotifications: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-notifications-new-publication"
              label="Alerta de Novas Publicações"
              description="Notificar quando novas publicações forem detectadas"
              checked={settings?.notifications?.newPublicationAlert || false}
              onChange={(checked) => updateNotificationSettings({ newPublicationAlert: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-notifications-deadlines"
              label="Lembretes de Prazo"
              description="Alertar sobre prazos próximos do vencimento"
              checked={settings?.notifications?.deadlineReminders || false}
              onChange={(checked) => updateNotificationSettings({ deadlineReminders: checked })}
              disabled={saving}
            />
            <ToggleRow
              id="settings-notifications-maintenance"
              label="Alertas de Manutenção"
              description="Notificar sobre manutenções programadas do sistema"
              checked={settings?.notifications?.systemMaintenanceAlerts || false}
              onChange={(checked) => updateNotificationSettings({ systemMaintenanceAlerts: checked })}
              disabled={saving}
            />
          </FormSection>
        </TabsContent>
      </Tabs>

      {saving && (
        <div
          role="status"
          className="fixed bottom-4 left-4 right-4 flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground shadow-lg sm:left-auto sm:justify-start"
        >
          <RefreshCw className="size-4 animate-spin" aria-hidden="true" />
          Salvando...
        </div>
      )}
    </div>
  )
}
